import clsx from 'clsx'
import { BpSelect } from '../../../../../shared/components/forms/select/BpSelect'
import { useTranslation } from 'react-i18next'
import { BpButton } from '../../../../../shared/components/buttons/bp-button/BpButton'
import { IconArrowLeft } from '../../../../../shared/components/icons/IconArrowLeft'
import { IconArrowRight } from '../../../../../shared/components/icons/IconArrowRight'
import { BpInput } from '../../../../../shared/components/forms/input/BpInput'
import styles from '../../../../../shared/components/cards/pagination/Pagination.module.scss'

const PAGE_SIZE_OPTIONS = [
  { value: '10', text: '10' },
  { value: '20', text: '20' },
  { value: '50', text: '50' },
  { value: '100', text: '100' },
  { value: '500', text: '500' },
]

export interface PaginationProps {
  page?: number
  pageCount?: number
  pageSize?: number
  total?: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

export const Pagination = ({
  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 1,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) => {
  const { t } = useTranslation()

  return (
    <div className={styles.root}>
      <div className={styles.layout}>
        <p className='whitespace-nowrap text-[15px] font-semibold text-focus-500'>
          {t('table.pagination.ofItems', {
            page: `${page * pageSize - pageSize + 1}–${page * pageSize > total ? total : page * pageSize}`,
            total: total,
          })}
        </p>
        <div className={styles.divider}></div>
        <div className={clsx('flex items-center gap-[8px]', styles.end)}>
          <BpSelect
            hideIcon
            required
            className='w-[102px]'
            dropPosition={'auto'}
            options={PAGE_SIZE_OPTIONS}
            showSearch={false}
            value={pageSize}
            valueColor='text-focus-500'
            onChange={(e) => {
              const size = Number(e.target.value)

              if (size !== pageSize) {
                onPageSizeChange(size)
              }
            }}
          ></BpSelect>
          <span className='whitespace-nowrap text-[15px] text-focus-500'>
            {t('table.pagination.itemsPerPage')}
          </span>
        </div>
        <div className={clsx(styles.divider, styles.dividerEdge)}></div>
        <div className={'flex items-center gap-[8px]'}>
          <div className={'w-[50px]'}>
            <BpInput
              max={pageCount}
              value={page}
              onChange={(e) => onPageChange(Number(e.target.value))}
            />
          </div>
          <span className='whitespace-nowrap text-[15px] text-focus-500'>
            {t('table.pagination.ofPages', { total: pageCount })}
          </span>
        </div>
        <div className={styles.divider}></div>
        <div data-ai-button-anchor className={clsx('flex items-center gap-[8px]', styles.end)}>
          <BpButton
            disabled={page <= 1}
            iconLeft={<IconArrowLeft />}
            theme='outline'
            onClick={() => onPageChange(page - 1)}
          >
            {t('table.pagination.previous')}
          </BpButton>
          <BpButton
            disabled={page >= pageCount}
            iconRight={<IconArrowRight />}
            theme='outline'
            onClick={() => onPageChange(page + 1)}
          >
            {t('table.pagination.next')}
          </BpButton>
        </div>
      </div>
    </div>
  )
}
